import React, { useState, useEffect, useCallback } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import {
  LayoutDashboard, Building2, GitBranch, CheckSquare, Plus, Search,
  ArrowLeft, TrendingUp, AlertTriangle, Clock, UserPlus, X, Briefcase,
  Download, Rocket, Users, FileText, Sparkles, BarChart3, Flame, Target,
  Plug, Receipt, Mail, RefreshCw, Check, Unplug, ShieldCheck, ShieldAlert, ArrowUpRight, Calendar,
} from "lucide-react";
import CommercialLoopPanel from "./CommercialLoopPanel";

const STAGES = ["Identified", "Qualified", "Discovery", "Solution Shaping",
  "Proposal Drafting", "Proposal Submitted", "Negotiation", "Verbal Approval",
  "Won", "Lost", "Deferred"];
const STATUSES = ["target", "prospect", "active_client", "past_client", "dormant"];
const STAKE = ["economic", "technical", "risk", "delivery", "influencer", "blocker", "champion"];

const gbp = (n) => "£" + (Number(n) || 0).toLocaleString();
const money = (v, on) => (on ? gbp(v) : "—");

const SUBTABS = [
  { key: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { key: "accounts", label: "Accounts", icon: Building2 },
  { key: "pipeline", label: "Pipeline", icon: GitBranch },
  { key: "loop", label: "Commercial Loop", icon: Target },
  { key: "delivery", label: "Delivery", icon: Briefcase },
  { key: "invoicing", label: "Invoicing", icon: Receipt },
  { key: "proposals", label: "Proposals", icon: FileText },
  { key: "associates", label: "Associates", icon: Users },
  { key: "intelligence", label: "Intelligence", icon: BarChart3 },
  { key: "integrations", label: "Integrations", icon: Plug },
  { key: "tasks", label: "Tasks", icon: CheckSquare },
];

export default function CRM360Panel({ me }) {
  const [sub, setSub] = useState("dashboard");
  const [activeAccount, setActiveAccount] = useState(null);
  const canWrite = me?.ui_flags?.crm_write !== false;
  const readAll = me?.ui_flags?.crm_read_all;
  const marginVisible = me?.ui_flags?.crm_margin_visible;

  const subtabs = SUBTABS.filter((t) => ((t.key !== "associates" && t.key !== "integrations") || readAll));

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get("tab") === "integrations") {
      setSub("integrations");
      if (p.get("qb") === "connected") toast.success("QuickBooks connected");
      if (p.get("qb") === "error") toast.error("QuickBooks connection failed");
      if (p.get("ms") === "connected") toast.success("Microsoft 365 connected");
      if (p.get("ms") === "error") toast.error("Microsoft 365 connection failed");
      const url = new URL(window.location.href);
      ["tab", "qb", "ms"].forEach((k) => url.searchParams.delete(k));
      window.history.replaceState({}, "", url.pathname + url.search);
    }
  }, []);

  return (
    <div data-testid="crm360-panel" className="space-y-6">
      <div>
        <div className="text-[11px] uppercase tracking-[0.18em] text-[#7737F6] font-semibold">Relationship & Engagement Hub</div>
        <h2 className="text-2xl font-bold ek-text mt-1">CRM360</h2>
        <p className="text-sm ek-text-muted mt-1">Manage the lifecycle from prospect to delivery and follow-on work.</p>
      </div>

      <div className="flex gap-1 border-b ek-line flex-wrap">
        {subtabs.map((t) => (
          <button key={t.key} data-testid={`crm-subtab-${t.key}`}
            onClick={() => { setSub(t.key); setActiveAccount(null); }}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${
              sub === t.key ? "border-[#7737F6] text-[#7737F6]" : "border-transparent ek-text-muted hover:text-zinc-800"}`}>
            <t.icon size={16} /> {t.label}
          </button>
        ))}
      </div>

      {sub === "dashboard" && <Dashboard canWrite={canWrite} onOpenAccount={(a) => { setActiveAccount(a); setSub("accounts"); }} onOpenPipeline={() => setSub("pipeline")} />}
      {sub === "accounts" && (
        activeAccount
          ? <AccountDetail id={activeAccount} onBack={() => setActiveAccount(null)} canWrite={canWrite} />
          : <Accounts onOpen={setActiveAccount} canWrite={canWrite} />
      )}
      {sub === "pipeline" && <Pipeline canWrite={canWrite} onOpenAccount={(a) => { setActiveAccount(a); setSub("accounts"); }} />}
      {sub === "loop" && <CommercialLoopPanel canWrite={canWrite} />}
      {sub === "delivery" && <Delivery canWrite={canWrite} marginVisible={marginVisible} />}
      {sub === "invoicing" && <InvoiceSchedule />}
      {sub === "proposals" && <Proposals canWrite={canWrite} />}
      {sub === "associates" && <Associates canWrite={canWrite} marginVisible={marginVisible} />}
      {sub === "intelligence" && <Intelligence onOpenAccount={(a) => { setActiveAccount(a); setSub("accounts"); }} />}
      {sub === "integrations" && <Integrations canWrite={canWrite} />}
      {sub === "tasks" && <Tasks canWrite={canWrite} />}
    </div>
  );
}

/* ------------------------------- Dashboard ------------------------------- */
function Dashboard({ onOpenAccount, onOpenPipeline, canWrite }) {
  const [d, setD] = useState(null);
  const [editT, setEditT] = useState(false);
  const [tval, setTval] = useState("");
  const [tperiod, setTperiod] = useState("quarter");
  const load = () => api.get("/admin/crm/dashboard").then(({ data }) => setD(data)).catch(() => setD(false));
  useEffect(() => { load(); }, []);
  const saveTarget = async () => {
    try {
      await api.put("/admin/crm/forecast-target", { target_gbp: Number(tval) || 0, period: tperiod });
      toast.success("Forecast target saved"); setEditT(false); load();
    } catch { toast.error("Could not save target"); }
  };
  if (d === null) return <Loading />;
  if (d === false) return <ErrorState />;
  const fc = d.forecast || {};
  const att = fc.attainment_pct;
  const attColor = att == null ? "bg-zinc-300" : att >= 100 ? "bg-emerald-500" : att >= 70 ? "bg-amber-500" : "bg-rose-500";
  return (
    <div className="space-y-6" data-testid="crm-dashboard">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Open pipeline" value={gbp(d.pipeline_value)} icon={TrendingUp} testid="stat-pipeline" />
        <Stat label="Weighted" value={gbp(d.weighted_pipeline)} icon={TrendingUp} testid="stat-weighted" />
        <Stat label="Open opportunities" value={d.open_count} icon={GitBranch} testid="stat-open" />
        <Stat label="Win rate" value={d.win_rate_pct == null ? "—" : d.win_rate_pct + "%"} icon={CheckSquare} testid="stat-winrate" />
      </div>

      {/* Weighted forecast vs target */}
      <div data-testid="crm-forecast-widget" className="border ek-line rounded-xl ek-surface p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2 text-sm font-semibold ek-text"><Target size={16} className="text-[#7737F6]" /> Weighted forecast vs target <span className="text-xs ek-text-muted font-normal">· this {fc.period}</span></div>
          {canWrite && !editT && <button data-testid="crm-forecast-edit" onClick={() => { setEditT(true); setTval(fc.target_gbp || ""); setTperiod(fc.period || "quarter"); }} className="text-xs text-[#7737F6] hover:underline">{fc.target_gbp ? "Edit target" : "Set target"}</button>}
        </div>
        {editT ? (
          <div className="flex flex-wrap gap-2 items-center">
            <input type="number" inputMode="decimal" data-testid="crm-forecast-target-input" placeholder="£ target" value={tval} onChange={(e) => setTval(e.target.value)} className={inp + " max-w-[180px]"} />
            <select value={tperiod} onChange={(e) => setTperiod(e.target.value)} data-testid="crm-forecast-period" className={inp + " max-w-[140px]"}>
              <option value="month">per month</option><option value="quarter">per quarter</option><option value="year">per year</option>
            </select>
            <button onClick={saveTarget} data-testid="crm-forecast-save" className="px-3 py-2 text-sm bg-[#7737F6] text-white rounded-lg">Save</button>
            <button onClick={() => setEditT(false)} className="px-3 py-2 text-sm ek-text-muted border ek-line rounded-lg">Cancel</button>
          </div>
        ) : fc.target_gbp ? (
          <div>
            <div className="flex justify-between text-xs ek-text-muted mb-1">
              <span>Weighted <b className="ek-text">{gbp(fc.weighted)}</b></span>
              <span data-testid="crm-forecast-attainment">{att == null ? "—" : att + "%"} of {gbp(fc.target_gbp)}</span>
            </div>
            <div className="relative h-3 rounded-full ek-surface-2 overflow-hidden">
              <div className={`absolute h-3 rounded-full ${attColor}`} style={{ width: `${Math.min(100, att || 0)}%` }} />
            </div>
            {fc.gap_gbp > 0 && <div className="text-[11px] text-amber-600 mt-1">{gbp(fc.gap_gbp)} short of target — grow or progress weighted pipeline to close the gap.</div>}
          </div>
        ) : <div className="text-xs ek-text-muted">No target set — set one to track weighted-forecast attainment.</div>}
      </div>

      {/* Forecast hygiene — stage-age & idle flags */}
      <Card title={`Forecast hygiene — open opportunities${d.opp_hygiene_flagged ? ` · ${d.opp_hygiene_flagged} flagged` : ""}${d.opp_no_next_step ? ` · ${d.opp_no_next_step} need a next step` : ""}`} icon={AlertTriangle} testid="crm-forecast-hygiene">
        {(!d.opp_hygiene || d.opp_hygiene.length === 0) ? <Empty text="No open opportunities" /> : (
          <div className="space-y-1.5">
            {d.opp_hygiene.slice(0, 12).map((h) => (
              <div key={h.id} data-testid={`crm-hygiene-${h.id}`} className="flex items-center justify-between gap-2 text-sm py-1">
                <button className="text-[#7737F6] hover:underline truncate text-left" onClick={onOpenPipeline}>{h.name}</button>
                <div className="flex items-center gap-1.5 shrink-0">
                  <span className="text-[11px] ek-text-muted">{h.stage}</span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-100 ek-text-muted" title="days in current stage">{h.stage_age_days ?? "—"}d stage</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded ${h.flags.includes("idle") ? "bg-rose-100 text-rose-700" : "bg-zinc-100 ek-text-muted"}`} title="days since last update">{h.idle_days ?? "—"}d idle</span>
                  {h.flags.includes("stuck") && <span data-testid={`crm-hygiene-stuck-${h.id}`} className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">stuck</span>}
                  {h.flags.includes("no_next_step") && <span data-testid={`crm-hygiene-nonext-${h.id}`} className="text-[10px] px-1.5 py-0.5 rounded bg-violet-100 text-violet-700" title="no scheduled next task">no next step</span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card title="Tasks due / overdue" icon={Clock} testid="card-tasks-due">
          {d.tasks_due.length === 0 ? <Empty text="Nothing due" /> : d.tasks_due.map((t) => (
            <Row key={t.id}><span className="truncate">{t.title}</span>
              <span className="text-xs ek-text-muted">{(t.due || "").slice(0, 10)}</span></Row>
          ))}
        </Card>
        <Card title="Coverage — needs attention" icon={AlertTriangle} testid="card-coverage">
          {[...d.accounts_no_touch.map((a) => ({ ...a, tag: "no touch" })),
            ...d.stale_accounts.map((a) => ({ ...a, tag: "stale >21d" }))].slice(0, 12).map((a) => (
            <Row key={a.id + a.tag}>
              <button className="text-[#7737F6] hover:underline truncate text-left" onClick={() => onOpenAccount(a.id)}
                data-testid={`coverage-open-${a.id}`}>{a.name}</button>
              <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">{a.tag}</span>
            </Row>
          ))}
          {d.accounts_no_touch.length + d.stale_accounts.length === 0 && <Empty text="All accounts recently touched" />}
        </Card>
      </div>
    </div>
  );
}

/* ------------------------------- Accounts -------------------------------- */
function Accounts({ onOpen, canWrite }) {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ name: "", sector: "", relationship_status: "prospect", website: "" });

  const load = useCallback(() => {
    api.get(`/admin/crm/accounts`, { params: { q } }).then(({ data }) => setRows(data.accounts)).catch(() => setRows([]));
  }, [q]);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.name.trim()) return toast.error("Name required");
    try {
      const { data } = await api.post("/admin/crm/accounts", form);
      toast.success("Account created");
      setShowNew(false); setForm({ name: "", sector: "", relationship_status: "prospect", website: "" });
      onOpen(data.id);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-4" data-testid="crm-accounts">
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 ek-text-muted" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search accounts…"
            data-testid="crm-account-search"
            className="w-full pl-9 pr-3 py-2 text-sm border ek-line rounded-lg focus:outline-none focus:border-[#7737F6]" />
        </div>
        {canWrite && <button onClick={() => setShowNew((v) => !v)} data-testid="crm-new-account-btn"
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-[#7737F6] text-white rounded-lg hover:bg-[#5D28C7]">
          <Plus size={16} /> New account</button>}
      </div>

      {showNew && (
        <div className="p-4 border ek-line rounded-lg ek-surface-2 grid sm:grid-cols-2 gap-3" data-testid="crm-new-account-form">
          <Field label="Company name"><input data-testid="crm-acc-name" value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })} className={inp} /></Field>
          <Field label="Sector"><input data-testid="crm-acc-sector" value={form.sector}
            onChange={(e) => setForm({ ...form, sector: e.target.value })} className={inp} /></Field>
          <Field label="Website"><input data-testid="crm-acc-website" value={form.website}
            onChange={(e) => setForm({ ...form, website: e.target.value })} className={inp} /></Field>
          <Field label="Status"><select data-testid="crm-acc-status" value={form.relationship_status}
            onChange={(e) => setForm({ ...form, relationship_status: e.target.value })} className={inp}>
            {STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}</select></Field>
          <div className="sm:col-span-2 flex gap-2">
            <button onClick={create} data-testid="crm-acc-save" className="px-4 py-2 text-sm bg-[#7737F6] text-white rounded-lg">Save</button>
            <button onClick={() => setShowNew(false)} className="px-4 py-2 text-sm ek-text-muted">Cancel</button>
          </div>
        </div>
      )}

      {rows === null ? <Loading /> : rows.length === 0 ? <Empty text="No accounts yet" /> : (
        <div className="border ek-line rounded-lg divide-y divide-zinc-100">
          {rows.map((a) => (
            <button key={a.id} onClick={() => onOpen(a.id)} data-testid={`crm-account-row-${a.id}`}
              className="w-full flex items-center justify-between px-4 py-3 hover:bg-zinc-50 text-left">
              <div><div className="font-medium ek-text">{a.name}</div>
                <div className="text-xs ek-text-muted">{a.sector || "—"} · {a.brand}</div></div>
              <span className="text-[11px] px-2 py-1 rounded-full bg-[#7737F6]/10 text-[#7737F6]">{(a.relationship_status || "").replace("_", " ")}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* --------------------------- Account detail ------------------------------ */
function AccountDetail({ id, onBack, canWrite }) {
  const [a, setA] = useState(null);
  const [tl, setTl] = useState([]);
  const load = useCallback(() => {
    api.get(`/admin/crm/accounts/${id}`).then(({ data }) => setA(data)).catch(() => setA(false));
    api.get(`/admin/crm/accounts/${id}/timeline`).then(({ data }) => setTl(data.timeline)).catch(() => {});
  }, [id]);
  useEffect(() => { load(); }, [load]);
  if (a === null) return <Loading />;
  if (a === false) return <ErrorState />;

  return (
    <div className="space-y-5" data-testid="crm-account-detail">
      <button onClick={onBack} data-testid="crm-account-back" className="flex items-center gap-1.5 text-sm ek-text-muted hover:text-zinc-800">
        <ArrowLeft size={15} /> Back to accounts</button>
      <div className="flex items-start justify-between">
        <div><h3 className="text-xl font-bold ek-text">{a.name}</h3>
          <div className="text-sm ek-text-muted">{a.sector || "—"} · {a.website || ""} · {(a.relationship_status || "").replace("_", " ")}</div></div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Contacts account={a} canWrite={canWrite} onReload={load} />
        <Opportunities account={a} canWrite={canWrite} onReload={load} />
      </div>
      <ActivityLog account={a} tl={tl} canWrite={canWrite} onReload={load} />
      <AccountPlan account={a} canWrite={canWrite} />
    </div>
  );
}

function Contacts({ account, canWrite, onReload }) {
  const [f, setF] = useState({ name: "", email: "", stakeholder_type: "influencer", title: "" });
  const [open, setOpen] = useState(false);
  const add = async () => {
    if (!f.name.trim()) return toast.error("Name required");
    try { await api.post(`/admin/crm/accounts/${account.id}/contacts`, f); toast.success("Contact added"); setOpen(false); setF({ name: "", email: "", stakeholder_type: "influencer", title: "" }); onReload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <Card title="Contacts" icon={UserPlus} testid="card-contacts"
      action={canWrite && <SmallBtn onClick={() => setOpen((v) => !v)} testid="crm-add-contact-btn">Add</SmallBtn>}>
      {open && (
        <div className="space-y-2 pb-3 mb-2 border-b ek-line" data-testid="crm-contact-form">
          <input placeholder="Name" data-testid="crm-contact-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={inp} />
          <input placeholder="Email" data-testid="crm-contact-email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={inp} />
          <div className="flex gap-2">
            <select value={f.stakeholder_type} data-testid="crm-contact-type" onChange={(e) => setF({ ...f, stakeholder_type: e.target.value })} className={inp}>
              {STAKE.map((s) => <option key={s} value={s}>{s}</option>)}</select>
            <button onClick={add} data-testid="crm-contact-save" className="px-3 py-1.5 text-sm bg-[#7737F6] text-white rounded-lg shrink-0">Save</button>
          </div>
        </div>
      )}
      {(account.contacts || []).length === 0 ? <Empty text="No contacts" /> : account.contacts.map((c) => (
        <Row key={c.id}><span className="truncate"><b className="font-medium">{c.name}</b> <span className="ek-text-muted">{c.title}</span></span>
          <span className="text-[11px] px-2 py-0.5 rounded-full ek-surface-2 ek-text-soft">{c.stakeholder_type}</span></Row>
      ))}
    </Card>
  );
}

function Opportunities({ account, canWrite, onReload }) {
  const [f, setF] = useState({ name: "", est_value: "", probability: "", stage: "Identified" });
  const [open, setOpen] = useState(false);
  const add = async () => {
    if (!f.name.trim()) return toast.error("Name required");
    try {
      await api.post(`/admin/crm/opportunities`, { account_id: account.id, name: f.name,
        est_value: Number(f.est_value) || 0, probability: Number(f.probability) || 0, stage: f.stage });
      toast.success("Opportunity created"); setOpen(false); setF({ name: "", est_value: "", probability: "", stage: "Identified" }); onReload();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const move = async (o, stage) => { try { await api.patch(`/admin/crm/opportunities/${o.id}`, { stage }); onReload(); } catch { toast.error("Failed"); } };
  const convert = async (o) => {
    try { const { data } = await api.post(`/admin/crm/opportunities/${o.id}/convert`); toast.success(data.created ? "Engagement created" : "Already converted"); onReload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <Card title="Opportunities" icon={GitBranch} testid="card-opps"
      action={canWrite && <SmallBtn onClick={() => setOpen((v) => !v)} testid="crm-add-opp-btn">Add</SmallBtn>}>
      {open && (
        <div className="space-y-2 pb-3 mb-2 border-b ek-line" data-testid="crm-opp-form">
          <input placeholder="Opportunity name" data-testid="crm-opp-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={inp} />
          <div className="flex gap-2">
            <input placeholder="£ value" type="number" inputMode="decimal" data-testid="crm-opp-value" value={f.est_value} onChange={(e) => setF({ ...f, est_value: e.target.value })} className={inp} />
            <input placeholder="% prob" type="number" inputMode="decimal" data-testid="crm-opp-prob" value={f.probability} onChange={(e) => setF({ ...f, probability: e.target.value })} className={inp} />
          </div>
          <div className="flex gap-2">
            <select value={f.stage} data-testid="crm-opp-stage" onChange={(e) => setF({ ...f, stage: e.target.value })} className={inp}>
              {STAGES.map((s) => <option key={s} value={s}>{s}</option>)}</select>
            <button onClick={add} data-testid="crm-opp-save" className="px-3 py-1.5 text-sm bg-[#7737F6] text-white rounded-lg shrink-0">Save</button>
          </div>
        </div>
      )}
      {(account.opportunities || []).length === 0 ? <Empty text="No opportunities" /> : account.opportunities.map((o) => (
        <div key={o.id} className="py-2 text-sm border-b border-zinc-50 last:border-0">
          <div className="flex items-center justify-between">
            <div className="truncate"><b className="font-medium">{o.name}</b><div className="text-xs ek-text-muted">{gbp(o.est_value)} · {o.probability}%</div></div>
            <select value={o.stage} disabled={!canWrite} onChange={(e) => move(o, e.target.value)} data-testid={`crm-opp-move-${o.id}`}
              className="text-xs border ek-line rounded px-2 py-1">{STAGES.map((s) => <option key={s}>{s}</option>)}</select>
          </div>
          {canWrite && o.stage === "Won" && !o.engagement_id && (
            <button onClick={() => convert(o)} data-testid={`crm-opp-convert-${o.id}`}
              className="mt-2 flex items-center gap-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 px-2.5 py-1 rounded-lg">
              <Rocket size={13} /> Convert to engagement</button>
          )}
          {o.engagement_id && <div className="mt-1 text-[11px] text-emerald-600 flex items-center gap-1"><Briefcase size={11} /> Engagement live</div>}
        </div>
      ))}
    </Card>
  );
}

function ActivityLog({ account, tl, canWrite, onReload }) {
  const [f, setF] = useState({ activity_type: "note", subject: "", body: "" });
  const [open, setOpen] = useState(false);
  const add = async () => {
    if (!f.subject.trim()) return toast.error("Subject required");
    try { await api.post(`/admin/crm/activities`, { account_id: account.id, ...f }); toast.success("Logged"); setOpen(false); setF({ activity_type: "note", subject: "", body: "" }); onReload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <Card title="Relationship timeline" icon={Clock} testid="card-timeline"
      action={canWrite && <SmallBtn onClick={() => setOpen((v) => !v)} testid="crm-log-activity-btn">Log</SmallBtn>}>
      {open && (
        <div className="space-y-2 pb-3 mb-2 border-b ek-line" data-testid="crm-activity-form">
          <div className="flex gap-2">
            <select value={f.activity_type} data-testid="crm-act-type" onChange={(e) => setF({ ...f, activity_type: e.target.value })} className={inp}>
              {["note", "call", "meeting", "email", "letter", "follow_up"].map((t) => <option key={t}>{t}</option>)}</select>
            <input placeholder="Subject" data-testid="crm-act-subject" value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} className={inp} />
          </div>
          <textarea placeholder="Notes" data-testid="crm-act-body" value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} className={inp} rows={2} />
          <button onClick={add} data-testid="crm-act-save" className="px-3 py-1.5 text-sm bg-[#7737F6] text-white rounded-lg">Save</button>
        </div>
      )}
      {tl.length === 0 ? <Empty text="No activity yet" /> : tl.map((x) => (
        <div key={x.id} className="flex gap-3 py-2 text-sm border-b border-zinc-50 last:border-0">
          <span className="text-[11px] px-2 py-0.5 rounded-full ek-surface-2 ek-text-soft h-fit shrink-0">{x._kind === "task" ? "task" : x.activity_type}</span>
          <div className="min-w-0"><div className="font-medium truncate">{x.subject || x.title}</div>
            {x.body && <div className="text-xs ek-text-muted truncate">{x.body}</div>}
            <div className="text-[11px] ek-text-muted">{(x._ts || "").slice(0, 16).replace("T", " ")}</div></div>
        </div>
      ))}
    </Card>
  );
}

/* ------------------------------- Pipeline -------------------------------- */
function Pipeline({ canWrite, onOpenAccount }) {
  const [rows, setRows] = useState(null);
  const load = () => api.get("/admin/crm/opportunities").then(({ data }) => setRows(data.opportunities)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);
  const move = async (o, stage) => { try { await api.patch(`/admin/crm/opportunities/${o.id}`, { stage }); load(); } catch { toast.error("Failed"); } };
  if (rows === null) return <Loading />;
  const open = STAGES.slice(0, 9);
  return (
    <div className="overflow-x-auto" data-testid="crm-pipeline">
      <div className="flex gap-3 min-w-max pb-2">
        {open.map((s) => {
          const col = rows.filter((o) => o.stage === s);
          const val = col.reduce((sum, o) => sum + (Number(o.est_value) || 0), 0);
          return (
            <div key={s} className="w-56 shrink-0" data-testid={`crm-stage-${s.replace(/ /g, "-")}`}>
              <div className="text-xs font-semibold ek-text-soft flex justify-between px-1 mb-2">
                <span>{s}</span><span className="ek-text-muted">{col.length}</span></div>
              <div className="text-[11px] text-[#7737F6] px-1 mb-2">{gbp(val)}</div>
              <div className="space-y-2">
                {col.map((o) => (
                  <div key={o.id} className="p-2.5 ek-surface border ek-line rounded-lg text-sm" data-testid={`crm-opp-card-${o.id}`}>
                    <div className="font-medium truncate">{o.name}</div>
                    <div className="text-xs ek-text-muted mb-1.5">{gbp(o.est_value)} · {o.probability}%</div>
                    <select value={o.stage} disabled={!canWrite} onChange={(e) => move(o, e.target.value)} data-testid={`crm-pipe-move-${o.id}`}
                      className="w-full text-[11px] border ek-line rounded px-1.5 py-1">{STAGES.map((x) => <option key={x}>{x}</option>)}</select>
                  </div>
                ))}
                {col.length === 0 && <div className="text-[11px] ek-text-muted px-1">—</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* -------------------------------- Tasks ---------------------------------- */
function Tasks({ canWrite }) {
  const [rows, setRows] = useState(null);
  const [title, setTitle] = useState("");
  const [due, setDue] = useState("");
  const load = () => api.get("/admin/crm/tasks").then(({ data }) => setRows(data.tasks)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);
  const add = async () => {
    if (!title.trim()) return toast.error("Title required");
    try { await api.post("/admin/crm/tasks", { title, due: due ? `${due}T09:00:00+00:00` : null }); toast.success("Task added"); setTitle(""); setDue(""); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const toggle = async (t) => { try { await api.patch(`/admin/crm/tasks/${t.id}`, { status: t.status === "done" ? "open" : "done" }); load(); } catch { toast.error("Failed"); } };
  if (rows === null) return <Loading />;
  return (
    <div className="space-y-4 max-w-2xl" data-testid="crm-tasks">
      {canWrite && (
        <div className="flex gap-2" data-testid="crm-task-form">
          <input placeholder="New task…" data-testid="crm-task-title" value={title} onChange={(e) => setTitle(e.target.value)} className={inp} />
          <input type="date" data-testid="crm-task-due" value={due} onChange={(e) => setDue(e.target.value)} className="px-3 py-2 text-sm border ek-line rounded-lg" />
          <button onClick={add} data-testid="crm-task-add" className="px-4 py-2 text-sm bg-[#7737F6] text-white rounded-lg shrink-0">Add</button>
        </div>
      )}
      {rows.length === 0 ? <Empty text="No tasks" /> : (
        <div className="border ek-line rounded-lg divide-y divide-zinc-100">
          {rows.map((t) => (
            <div key={t.id} className="flex items-center gap-3 px-4 py-2.5 text-sm" data-testid={`crm-task-${t.id}`}>
              <input type="checkbox" checked={t.status === "done"} onChange={() => toggle(t)} data-testid={`crm-task-toggle-${t.id}`} className="accent-[#7737F6]" />
              <span className={`flex-1 ${t.status === "done" ? "line-through ek-text-muted" : ""}`}>{t.title}</span>
              <span className="text-xs ek-text-muted">{(t.due || "").slice(0, 10)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ------------------------------- Delivery / Billing --------------------- */
function Delivery({ canWrite, marginVisible }) {
  const [engs, setEngs] = useState(null);
  const [active, setActive] = useState(null);
  useEffect(() => {
    api.get("/admin/crm/opportunities", { params: { stage: "Won" } })
      .then(({ data }) => setEngs(data.opportunities.filter((o) => o.engagement_id)))
      .catch(() => setEngs([]));
  }, []);
  if (active) return <BillingDetail opp={active} onBack={() => setActive(null)} canWrite={canWrite} marginVisible={marginVisible} />;
  if (engs === null) return <Loading />;
  return (
    <div className="space-y-4" data-testid="crm-delivery">
      {engs.length === 0 ? <Empty text="No live engagements yet — convert a Won opportunity" /> : (
        <div className="border ek-line rounded-lg divide-y divide-zinc-100">
          {engs.map((o) => (
            <button key={o.id} onClick={() => setActive(o)} data-testid={`crm-engagement-row-${o.engagement_id}`}
              className="w-full flex items-center justify-between px-4 py-3 hover:bg-zinc-50 text-left">
              <div><div className="font-medium ek-text">{o.name}</div>
                <div className="text-xs ek-text-muted">{gbp(o.est_value)} · engagement {o.engagement_id.slice(0, 14)}</div></div>
              <Briefcase size={16} className="text-[#7737F6]" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function InvoiceSchedule() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/crm/invoice-schedule", { params: { months: 6 } }).then(({ data }) => setD(data)).catch(() => setD(false)); }, []);
  if (d === null) return <Loading />;
  if (d === false) return <ErrorState />;
  const max = Math.max(...d.months.map((m) => m.total), 1);
  return (
    <div className="space-y-5" data-testid="crm-invoice-schedule">
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <Stat label="Next 6 months (billable)" value={gbp(d.window_total)} icon={Receipt} testid="invsched-window" />
        <Stat label="All upcoming milestones" value={gbp(d.all_upcoming_total)} icon={TrendingUp} testid="invsched-all" />
        <Stat label="Unscheduled (no date)" value={`${gbp(d.unscheduled.total)} · ${d.unscheduled.count}`} icon={AlertTriangle} testid="invsched-unscheduled" />
      </div>

      <div className="border ek-line rounded-xl ek-surface p-4" data-testid="crm-invsched-chart">
        <div className="text-sm font-semibold ek-text mb-3">Billable revenue forecast · next 6 months</div>
        <div className="flex items-end gap-3 h-40">
          {d.months.map((m) => (
            <div key={m.month} className="flex-1 flex flex-col items-center justify-end h-full" data-testid={`invsched-bar-${m.month}`}>
              <span className="text-[11px] font-semibold ek-text mb-1">{m.total ? gbp(m.total) : ""}</span>
              <div className="w-full rounded-t bg-[#7737F6]/80 hover:bg-[#7737F6] transition-colors" style={{ height: `${Math.max(2, (m.total / max) * 120)}px` }} title={`${m.label}: ${gbp(m.total)} · ${m.count} milestone(s)`} />
              <span className="text-[10px] ek-text-muted mt-1.5 text-center">{m.label}</span>
            </div>
          ))}
        </div>
      </div>

      {d.months.map((m) => m.items.length > 0 && (
        <Card key={m.month} title={`${m.label} · ${gbp(m.total)}`} icon={Calendar} testid={`invsched-month-${m.month}`}>
          {m.items.map((it, i) => (
            <Row key={i}>
              <span className="truncate"><b className="font-medium">{it.client_name}</b> <span className="ek-text-muted">— {it.title}</span></span>
              <span className="flex items-center gap-2 shrink-0"><span className="text-[10px] ek-text-muted">{it.due}</span><span className="font-medium">{gbp(it.fee)}</span></span>
            </Row>
          ))}
        </Card>
      ))}
      {d.window_total === 0 && <Empty text="No scheduled milestone fees in the next 6 months" />}
    </div>
  );
}

function CashflowPreview({ milestones }) {
  const dated = milestones.filter((m) => (m.due || "").length >= 7 && Number(m.fee) > 0);
  if (dated.length < 2) return null;
  const buckets = {};
  dated.forEach((m) => { const k = m.due.slice(0, 7); buckets[k] = (buckets[k] || 0) + Number(m.fee); });
  const keys = Object.keys(buckets).sort();
  const max = Math.max(...Object.values(buckets), 1);
  const fmtM = (k) => { const [y, mo] = k.split("-"); return new Date(y, mo - 1, 1).toLocaleString("en-GB", { month: "short", year: "2-digit" }); };
  return (
    <div className="mb-3 rounded-lg ek-surface-2 border ek-line px-3 py-2.5" data-testid="crm-cashflow-preview">
      <div className="text-[10px] uppercase tracking-[0.12em] ek-text-muted mb-2 flex items-center gap-1"><Calendar size={11} /> Cashflow shape · milestone fees by month</div>
      <div className="flex items-end gap-2 h-16">
        {keys.map((k) => (
          <div key={k} className="flex-1 flex flex-col items-center justify-end h-full" title={`${fmtM(k)}: ${gbp(buckets[k])}`}>
            <div className="w-full max-w-[36px] rounded-t bg-[#7737F6]/70" style={{ height: `${Math.max(3, (buckets[k] / max) * 46)}px` }} />
            <span className="text-[9px] ek-text-muted mt-1">{fmtM(k)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function MilestoneRow({ m, canWrite, onPatch, onStatus }) {
  const [fee, setFee] = useState(m.fee ?? 0);
  const [due, setDue] = useState((m.due || "").slice(0, 10));
  const tweaked = m.plan_fee != null && (Number(m.fee) !== Number(m.plan_fee) || (m.due || "").slice(0, 10) !== (m.plan_due || "").slice(0, 10));
  return (
    <div className="flex items-center justify-between gap-2 py-2 text-sm border-b border-zinc-50 last:border-0" data-testid={`crm-milestone-${m.id}`}>
      <div className="min-w-0 flex-1">
        <b className="font-medium">{m.title}</b>
        {m.seeded && <span className="ml-1.5 text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#7737F6]/10 text-[#7737F6]">from plan{tweaked ? " · edited" : ""}</span>}
        <div className="text-xs ek-text-muted flex items-center gap-2 mt-0.5">
          {m.qbo_doc_number && <span className="text-emerald-600">QB #{m.qbo_doc_number}</span>}
          {m.payment_status && <span className={`px-1.5 py-0.5 rounded-full text-[10px] ${m.payment_status === "paid" ? "bg-emerald-100 text-emerald-700" : m.payment_status === "partially_paid" ? "bg-amber-100 text-amber-700" : "ek-surface-2 ek-text-muted"}`}>{m.payment_status.replace(/_/g, " ")}</span>}
        </div>
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        <span className="text-xs ek-text-muted">£</span>
        <input type="number" inputMode="decimal" disabled={!canWrite} value={fee} data-testid={`crm-ms-fee-${m.id}`}
          onChange={(e) => setFee(e.target.value)}
          onBlur={() => { if (Number(fee) !== Number(m.fee)) onPatch(m, { fee: Number(fee) || 0 }); }}
          className="w-24 px-2 py-1 text-xs border ek-line rounded" />
        <input type="date" disabled={!canWrite} value={due} data-testid={`crm-ms-due-${m.id}`}
          onChange={(e) => setDue(e.target.value)}
          onBlur={() => { if (due !== (m.due || "").slice(0, 10)) onPatch(m, { due }); }}
          className="px-2 py-1 text-xs border ek-line rounded" />
        <select value={m.status} disabled={!canWrite} onChange={(e) => onStatus(m, e.target.value)} data-testid={`crm-ms-status-${m.id}`}
          className="text-xs border ek-line rounded px-2 py-1">
          {["not_started", "in_progress", "delivered", "client_approved", "ready_to_invoice", "invoiced"].map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
        </select>
      </div>
    </div>
  );
}

function BillingDetail({ opp, onBack, canWrite, marginVisible }) {
  const eid = opp.engagement_id;
  const [b, setB] = useState(null);
  const [days, setDays] = useState([]);
  const [qbConnected, setQbConnected] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [ms, setMs] = useState({ title: "", fee: "", status: "not_started" });
  const [dl, setDl] = useState({ date: "", billable_days: "1", day_rate: "", cost_rate: "", note: "" });
  const load = useCallback(() => {
    api.get(`/admin/crm/engagements/${eid}/billing`).then(({ data }) => setB(data)).catch(() => setB(false));
    api.get(`/admin/crm/engagements/${eid}/daylogs`).then(({ data }) => setDays(data.daylogs)).catch(() => {});
    api.get(`/admin/crm/integrations/status`).then(({ data }) => setQbConnected(!!data.quickbooks?.connected)).catch(() => {});
  }, [eid]);
  useEffect(() => { load(); }, [load]);

  const pushInvoice = async () => {
    setPushing(true);
    try {
      const { data } = await api.post(`/admin/crm/integrations/quickbooks/engagements/${eid}/push-invoice`);
      toast.success(`Invoice ${data.doc_number || data.invoice_id} pushed to QuickBooks (${gbp(data.total)})`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Push failed"); } finally { setPushing(false); }
  };

  const addMs = async () => {
    if (!ms.title.trim()) return toast.error("Title required");
    try { await api.post(`/admin/crm/engagements/${eid}/milestones`, { title: ms.title, fee: Number(ms.fee) || 0, status: ms.status }); toast.success("Milestone added"); setMs({ title: "", fee: "", status: "not_started" }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const moveMs = async (m, status) => { try { await api.patch(`/admin/crm/engagements/${eid}/milestones/${m.id}`, { status }); load(); } catch { toast.error("Failed"); } };
  const patchMs = async (m, patch) => { try { await api.patch(`/admin/crm/engagements/${eid}/milestones/${m.id}`, patch); load(); } catch { toast.error("Could not update milestone"); } };
  const resetPlan = async (profile) => {
    try {
      const { data } = await api.post(`/admin/crm/engagements/${eid}/milestones/reset-plan`, profile ? { fee_profile: profile } : {});
      toast.success(profile ? `Rebalanced (${profile}-loaded) — ${data.reset} milestone(s)` : `Reset to plan — ${data.reset} milestone(s)`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not reset plan"); }
  };
  const addDay = async () => {
    if (!dl.date) return toast.error("Date required");
    try { await api.post(`/admin/crm/engagements/${eid}/daylogs`, { date: dl.date, billable_days: Number(dl.billable_days) || 1, day_rate: Number(dl.day_rate) || 0, cost_rate: Number(dl.cost_rate) || 0, note: dl.note }); toast.success("Day logged"); setDl({ date: "", billable_days: "1", day_rate: "", cost_rate: "", note: "" }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const exportCsv = async () => {
    try {
      const res = await api.get(`/admin/crm/engagements/${eid}/invoice-export.csv`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = `invoice_${eid}.csv`; a.click();
      URL.revokeObjectURL(url); toast.success("Exported for QuickBooks"); load();
    } catch { toast.error("Export failed"); }
  };
  if (b === null) return <Loading />;
  if (b === false) return <ErrorState />;

  return (
    <div className="space-y-5" data-testid="crm-billing-detail">
      <button onClick={onBack} data-testid="crm-billing-back" className="flex items-center gap-1.5 text-sm ek-text-muted hover:text-zinc-800"><ArrowLeft size={15} /> Back to delivery</button>
      <div><h3 className="text-xl font-bold ek-text">{b.client_name || opp.name}</h3>
        <div className="text-sm ek-text-muted">Commercials & billing</div></div>

      {b.follow_on_ready && (
        <div className="p-3 rounded-lg bg-emerald-50 text-emerald-800 text-sm flex items-center gap-2" data-testid="crm-followon-prompt">
          <Rocket size={16} /> All milestones invoiced — log a follow-on opportunity or capture a case study.</div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Total fee" value={gbp(b.total_fee)} icon={TrendingUp} testid="bill-total" />
        <Stat label="Ready to invoice" value={gbp(b.ready_to_invoice_value)} icon={Clock} testid="bill-ready" />
        <Stat label="Invoiced" value={gbp(b.invoiced_value)} icon={CheckSquare} testid="bill-invoiced" />
        {marginVisible
          ? <Stat label="Gross margin" value={b.gross_margin_pct == null ? gbp(b.gross_margin || 0) : `${b.gross_margin_pct}%`} icon={TrendingUp} testid="bill-margin" />
          : <Stat label="Days logged" value={b.total_days} icon={Clock} testid="bill-days" />}
      </div>

      <Card title="Milestones" icon={CheckSquare} testid="card-milestones"
        action={canWrite && (
          <div className="flex items-center gap-3 flex-wrap">
            {(b.milestones || []).some((m) => m.seeded || m.plan_fee != null) && (
              <div className="flex items-center gap-1" data-testid="crm-reset-plan">
                <select data-testid="crm-reset-plan-profile" defaultValue=""
                  onChange={(e) => { const v = e.target.value; e.target.value = ""; if (v) resetPlan(v === "restore" ? null : v); }}
                  className="text-xs border ek-line rounded px-2 py-1.5">
                  <option value="">Reset / rebalance plan…</option>
                  <option value="restore">Reset to plan (undo tweaks)</option>
                  <option value="even">Rebalance · even</option>
                  <option value="front">Rebalance · front-loaded</option>
                  <option value="back">Rebalance · back-loaded</option>
                </select>
              </div>
            )}
            {qbConnected && <button onClick={pushInvoice} disabled={pushing} data-testid="crm-push-quickbooks"
              className="flex items-center gap-1.5 text-xs font-medium text-white bg-emerald-600 hover:bg-emerald-700 px-3 py-1.5 rounded-lg disabled:opacity-50">
              <Receipt size={13} /> {pushing ? "Pushing…" : "Push invoice"}</button>}
            <button onClick={exportCsv} data-testid="crm-export-quickbooks" className="flex items-center gap-1.5 text-xs font-medium text-[#7737F6] hover:text-[#5D28C7]"><Download size={13} /> Export CSV</button>
          </div>)}>
        {canWrite && (
          <div className="flex gap-2 pb-3 mb-2 border-b ek-line" data-testid="crm-milestone-form">
            <input placeholder="Milestone" data-testid="crm-ms-title" value={ms.title} onChange={(e) => setMs({ ...ms, title: e.target.value })} className={inp} />
            <input placeholder="£ fee" type="number" inputMode="decimal" data-testid="crm-ms-fee" value={ms.fee} onChange={(e) => setMs({ ...ms, fee: e.target.value })} className="w-28 px-3 py-2 text-sm border ek-line rounded-lg" />
            <button onClick={addMs} data-testid="crm-ms-save" className="px-3 py-1.5 text-sm bg-[#7737F6] text-white rounded-lg shrink-0">Add</button>
          </div>
        )}
        <p className="text-[11px] ek-text-muted mb-2">Client-approved milestones are marked <b>ready-to-invoice</b> automatically when you export to QuickBooks.</p>
        <CashflowPreview milestones={b.milestones || []} />
        {(b.milestones || []).length === 0 ? <Empty text="No milestones" /> : b.milestones.map((m) => (
          <MilestoneRow key={m.id + (m.updated_at || "")} m={m} canWrite={canWrite} onPatch={patchMs} onStatus={moveMs} />
        ))}
      </Card>

      <Card title="Day log (billing)" icon={Clock} testid="card-daylogs">
        {canWrite && (
          <div className="flex flex-wrap gap-2 pb-3 mb-2 border-b ek-line" data-testid="crm-daylog-form">
            <input type="date" data-testid="crm-day-date" value={dl.date} onChange={(e) => setDl({ ...dl, date: e.target.value })} className="px-3 py-2 text-sm border ek-line rounded-lg" />
            <input placeholder="days" type="number" inputMode="decimal" data-testid="crm-day-days" value={dl.billable_days} onChange={(e) => setDl({ ...dl, billable_days: e.target.value })} className="w-20 px-3 py-2 text-sm border ek-line rounded-lg" />
            {marginVisible && <input placeholder="cost/day" type="number" inputMode="decimal" data-testid="crm-day-cost" value={dl.cost_rate} onChange={(e) => setDl({ ...dl, cost_rate: e.target.value })} className="w-28 px-3 py-2 text-sm border ek-line rounded-lg" />}
            <button onClick={addDay} data-testid="crm-day-save" className="px-3 py-1.5 text-sm bg-[#7737F6] text-white rounded-lg shrink-0">Log</button>
          </div>
        )}
        {days.length === 0 ? <Empty text="No days logged" /> : days.map((d) => (
          <Row key={d.id}><span>{(d.date || "").slice(0, 10)} · {d.person_email}</span><span className="ek-text-muted">{d.billable_days} day(s)</span></Row>
        ))}
      </Card>
    </div>
  );
}

/* ------------------------------- Proposals ------------------------------ */
function Proposals({ canWrite }) {
  const [opps, setOpps] = useState([]);
  const [oppId, setOppId] = useState("");
  const [rows, setRows] = useState([]);
  const [title, setTitle] = useState("");
  const [proof, setProof] = useState(null);
  useEffect(() => { api.get("/admin/crm/opportunities").then(({ data }) => setOpps(data.opportunities)).catch(() => {}); }, []);
  const load = useCallback(() => {
    if (!oppId) { setRows([]); return; }
    api.get("/admin/crm/proposals", { params: { opportunity_id: oppId } }).then(({ data }) => setRows(data.proposals)).catch(() => setRows([]));
  }, [oppId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!oppId) { setProof(null); return; }
    let stale = false;
    api.get(`/admin/crm/opportunities/${oppId}/proof-brief`).then(({ data }) => { if (!stale) setProof(data); }).catch(() => { if (!stale) setProof(null); });
    return () => { stale = true; };
  }, [oppId]);

  const create = async () => {
    if (!oppId) return toast.error("Pick an opportunity");
    if (!title.trim()) return toast.error("Title required");
    try { await api.post("/admin/crm/proposals", { opportunity_id: oppId, title }); toast.success("Proposal created"); setTitle(""); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="space-y-4 max-w-3xl" data-testid="crm-proposals">
      <div className="flex gap-2 flex-wrap">
        <select value={oppId} onChange={(e) => setOppId(e.target.value)} data-testid="crm-prop-opp-select" className={inp}>
          <option value="">Select opportunity…</option>
          {opps.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
        {canWrite && oppId && (
          <div className="flex gap-2 flex-1">
            <input placeholder="New proposal title" data-testid="crm-prop-title" value={title} onChange={(e) => setTitle(e.target.value)} className={inp} />
            <button onClick={create} data-testid="crm-prop-create" className="px-4 py-2 text-sm bg-[#7737F6] text-white rounded-lg shrink-0">Create</button>
          </div>
        )}
      </div>
      {proof?.linked && proof.upgrade_path?.recommend && (
        <div data-testid="crm-proof-brief" className="rounded-xl border border-[#7737F6]/40 bg-[#7737F6]/[0.05] p-4">
          <div className="flex items-center gap-1.5 text-[11px] tracking-[0.14em] uppercase text-[#7737F6] font-semibold mb-1.5">
            <ShieldAlert size={13} /> Proof-led selling · {proof.upgrade_path.from} → {proof.upgrade_path.to}
          </div>
          <p data-testid="crm-proof-headline" className="text-[13px] ek-text-soft leading-snug">{proof.upgrade_path.headline}</p>
          <div className="flex flex-wrap gap-5 mt-3">
            <div><div className="text-[10px] uppercase tracking-[0.12em] ek-text-muted">Self-declared</div>
              <div className="text-base font-bold ek-text">{proof.upgrade_path.self_declared_count}/{proof.upgrade_path.streams_total}</div></div>
            <div><div className="text-[10px] uppercase tracking-[0.12em] ek-text-muted">Evidenced</div>
              <div className="text-base font-bold ek-text">{proof.upgrade_path.evidenced_streams}/{proof.upgrade_path.streams_total}</div></div>
            {proof.upgrade_path.value_at_stake && (
              <div><div className="text-[10px] uppercase tracking-[0.12em] ek-text-muted">£-at-stake <span className="normal-case tracking-normal text-amber-600">· internal</span></div>
                <div data-testid="crm-proof-value" className="text-base font-bold text-[#7737F6]">{proof.upgrade_path.value_at_stake.range_label}</div></div>
            )}
          </div>
        </div>
      )}
      {oppId && rows.length === 0 && <Empty text="No proposals for this opportunity" />}
      {rows.map((p) => <ProposalCard key={p.id} p={p} canWrite={canWrite} onReload={load} />)}
    </div>
  );
}

function ProposalCard({ p, canWrite, onReload }) {
  const [busy, setBusy] = useState(false);
  const [local, setLocal] = useState(p);
  useEffect(() => { setLocal(p); }, [p]);
  const aiDraft = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/admin/crm/proposals/${p.id}/ai-draft`); setLocal(data); toast.success("AI draft ready — review & edit"); }
    catch (e) { toast.error(e.response?.data?.detail || "AI draft failed"); } finally { setBusy(false); }
  };
  const setStatus = async (status) => { try { await api.patch(`/admin/crm/proposals/${p.id}`, { status }); toast.success(status); onReload(); } catch { toast.error("Failed"); } };
  const newVersion = async () => { try { await api.post(`/admin/crm/proposals/${p.id}/version`); toast.success("New version created"); onReload(); } catch { toast.error("Failed"); } };
  const exportPdf = async () => {
    try {
      const res = await api.get(`/admin/crm/proposals/${p.id}/export.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = `proposal_${p.id}.pdf`; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };
  return (
    <div className="border ek-line rounded-xl ek-surface p-4" data-testid={`crm-proposal-${p.id}`}>
      <div className="flex items-center justify-between">
        <div><div className="font-semibold ek-text">{local.title} <span className="text-xs ek-text-muted">v{local.version}</span></div>
          <div className="text-xs ek-text-muted">{local.status}{local.ai_generated ? " · AI-drafted" : ""}{local.superseded_by ? " · superseded" : ""}</div></div>
        <span className="text-[11px] px-2 py-1 rounded-full bg-[#7737F6]/10 text-[#7737F6]">{local.status}</span>
      </div>
      {(local.discovery_summary || local.scope) && (
        <p className="text-sm ek-text-soft mt-2 line-clamp-3">{local.discovery_summary || local.scope}</p>
      )}
      {local.evidence_position && (
        <div data-testid={`crm-prop-evidence-${p.id}`} className="mt-2 rounded-lg bg-[#7737F6]/5 border border-[#7737F6]/20 px-3 py-2">
          <div className="text-[10px] font-semibold tracking-[0.12em] uppercase text-[#7737F6] mb-1 flex items-center gap-1"><ShieldCheck size={11} /> Where you are &amp; what we&apos;ll prove{local.evidence_brief ? " · evidence-grounded" : ""}</div>
          <p className="text-[13px] ek-text-soft line-clamp-4">{local.evidence_position}</p>
        </div>
      )}
      {local.value_at_stake && (
        <div data-testid={`crm-prop-value-at-stake-${p.id}`} className="mt-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2">
          <div className="text-[10px] font-semibold tracking-[0.12em] uppercase text-amber-700 mb-0.5 flex items-center gap-1">
            <Target size={11} /> £-at-stake · internal only — not shown to client
          </div>
          <div className="text-[15px] font-bold text-amber-800">{local.value_at_stake.range_label}
            <span className="text-[11px] font-normal text-amber-700"> · midpoint {local.value_at_stake.headline_label} over {local.value_at_stake.horizon_years}y</span></div>
          <div className="text-[10px] text-amber-600 mt-0.5">Basis: {local.value_at_stake.annual_value_label || local.value_at_stake.annual_value_base_label} value base × {local.value_at_stake.gap_factor_pct}% maturity gap × improvement leverage. Indicative, not a benefits commitment.</div>
        </div>
      )}
      <div className="flex gap-2 flex-wrap mt-3">
        {canWrite && <button onClick={aiDraft} disabled={busy} data-testid={`crm-prop-aidraft-${p.id}`}
          className="flex items-center gap-1.5 text-xs font-medium text-white bg-[#7737F6] hover:bg-[#5D28C7] px-3 py-1.5 rounded-lg disabled:opacity-50">
          <Sparkles size={13} /> {busy ? "Drafting…" : "AI draft"}</button>}
        <button onClick={exportPdf} data-testid={`crm-prop-export-${p.id}`} className="flex items-center gap-1.5 text-xs font-medium ek-text-soft border ek-line px-3 py-1.5 rounded-lg hover:bg-zinc-50"><Download size={13} /> Export PDF</button>
        {canWrite && <button onClick={() => setStatus("sent")} data-testid={`crm-prop-send-${p.id}`} className="text-xs font-medium ek-text-soft border ek-line px-3 py-1.5 rounded-lg hover:bg-zinc-50">Mark sent</button>}
        {canWrite && <button onClick={newVersion} data-testid={`crm-prop-version-${p.id}`} className="text-xs font-medium ek-text-soft border ek-line px-3 py-1.5 rounded-lg hover:bg-zinc-50">New version</button>}
      </div>
    </div>
  );
}

/* ------------------------------- Associates ----------------------------- */
function Associates({ canWrite, marginVisible }) {
  const [rows, setRows] = useState(null);
  const [active, setActive] = useState(null);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", type: "individual", email: "", day_rate: "", cost_rate: "", conflict_flags: "" });
  const load = () => api.get("/admin/crm/associates").then(({ data }) => setRows(data.associates)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);
  const create = async () => {
    if (!f.name.trim()) return toast.error("Name required");
    try {
      await api.post("/admin/crm/associates", { name: f.name, type: f.type, email: f.email,
        day_rate: Number(f.day_rate) || 0, cost_rate: Number(f.cost_rate) || 0,
        conflict_flags: f.conflict_flags ? f.conflict_flags.split(",").map((s) => s.trim()).filter(Boolean) : [] });
      toast.success("Associate added"); setOpen(false); setF({ name: "", type: "individual", email: "", day_rate: "", cost_rate: "", conflict_flags: "" }); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  if (active) return <AssociateDetail assoc={active} onBack={() => setActive(null)} canWrite={canWrite} marginVisible={marginVisible} />;
  if (rows === null) return <Loading />;
  return (
    <div className="space-y-4" data-testid="crm-associates">
      {canWrite && <button onClick={() => setOpen((v) => !v)} data-testid="crm-new-associate-btn" className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-[#7737F6] text-white rounded-lg"><Plus size={16} /> New associate</button>}
      {open && (
        <div className="p-4 border ek-line rounded-lg ek-surface-2 grid sm:grid-cols-2 gap-3" data-testid="crm-associate-form">
          <Field label="Name"><input data-testid="crm-assoc-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={inp} /></Field>
          <Field label="Type"><select data-testid="crm-assoc-type" value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })} className={inp}><option value="individual">Individual</option><option value="company">Company</option></select></Field>
          <Field label="Email"><input data-testid="crm-assoc-email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={inp} /></Field>
          <Field label="Day rate (£)"><input type="number" inputMode="decimal" data-testid="crm-assoc-dayrate" value={f.day_rate} onChange={(e) => setF({ ...f, day_rate: e.target.value })} className={inp} /></Field>
          {marginVisible && <Field label="Cost rate (£)"><input type="number" inputMode="decimal" data-testid="crm-assoc-costrate" value={f.cost_rate} onChange={(e) => setF({ ...f, cost_rate: e.target.value })} className={inp} /></Field>}
          <Field label="Conflict flags (comma-sep)"><input data-testid="crm-assoc-conflicts" value={f.conflict_flags} onChange={(e) => setF({ ...f, conflict_flags: e.target.value })} className={inp} /></Field>
          <div className="sm:col-span-2"><button onClick={create} data-testid="crm-assoc-save" className="px-4 py-2 text-sm bg-[#7737F6] text-white rounded-lg">Save</button></div>
        </div>
      )}
      {rows.length === 0 ? <Empty text="No associates yet" /> : (
        <div className="border ek-line rounded-lg divide-y divide-zinc-100">
          {rows.map((a) => (
            <button key={a.id} onClick={() => setActive(a)} data-testid={`crm-associate-row-${a.id}`} className="w-full flex items-center justify-between px-4 py-3 hover:bg-zinc-50 text-left">
              <div><div className="font-medium ek-text">{a.name}</div><div className="text-xs ek-text-muted">{a.type} · {a.email || "—"}</div></div>
              <span className="text-[11px] px-2 py-1 rounded-full ek-surface-2 ek-text-soft">{a.type}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function AssociateDetail({ assoc, onBack, canWrite, marginVisible }) {
  const [assignments, setAssignments] = useState([]);
  const [engs, setEngs] = useState([]);
  const [roll, setRoll] = useState(null);
  const [engId, setEngId] = useState("");
  const load = useCallback(() => {
    api.get(`/admin/crm/associates/${assoc.id}/assignments`).then(({ data }) => setAssignments(data.assignments)).catch(() => {});
    api.get(`/admin/crm/associates/${assoc.id}/rollup`).then(({ data }) => setRoll(data)).catch(() => {});
    api.get("/admin/crm/opportunities", { params: { stage: "Won" } }).then(({ data }) => setEngs(data.opportunities.filter((o) => o.engagement_id))).catch(() => {});
  }, [assoc.id]);
  useEffect(() => { load(); }, [load]);
  const assign = async () => {
    if (!engId) return toast.error("Pick an engagement");
    try {
      const { data } = await api.post(`/admin/crm/associates/${assoc.id}/assignments`, { engagement_id: engId, engagement_role: "delivery_contributor" });
      if (data.conflict_warning) toast.warning(`Conflict flag: ${data.conflict_warning.join(", ")}`);
      else toast.success("Assigned"); setEngId(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="space-y-5" data-testid="crm-associate-detail">
      <button onClick={onBack} data-testid="crm-associate-back" className="flex items-center gap-1.5 text-sm ek-text-muted hover:text-zinc-800"><ArrowLeft size={15} /> Back</button>
      <div><h3 className="text-xl font-bold ek-text">{assoc.name}</h3><div className="text-sm ek-text-muted">{assoc.type} · {assoc.email}</div></div>
      {roll && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Stat label="Days logged" value={roll.total_days} icon={Clock} testid="roll-days" />
          <Stat label="Billed value" value={gbp(roll.total_value)} icon={TrendingUp} testid="roll-value" />
          {marginVisible && <Stat label="Cost" value={gbp(roll.total_cost)} icon={TrendingUp} testid="roll-cost" />}
          {marginVisible && <Stat label="Margin" value={gbp(roll.margin)} icon={TrendingUp} testid="roll-margin" />}
        </div>
      )}
      <Card title="Engagement assignments" icon={Briefcase} testid="card-assignments">
        {canWrite && (
          <div className="flex gap-2 pb-3 mb-2 border-b ek-line" data-testid="crm-assign-form">
            <select value={engId} onChange={(e) => setEngId(e.target.value)} data-testid="crm-assign-select" className={inp}>
              <option value="">Select engagement…</option>
              {engs.map((o) => <option key={o.engagement_id} value={o.engagement_id}>{o.name}</option>)}
            </select>
            <button onClick={assign} data-testid="crm-assign-save" className="px-3 py-1.5 text-sm bg-[#7737F6] text-white rounded-lg shrink-0">Assign</button>
          </div>
        )}
        {assignments.length === 0 ? <Empty text="No assignments" /> : assignments.map((a) => (
          <Row key={a.id}><span>{a.client_name || a.engagement_id} · {a.engagement_role.replace(/_/g, " ")}</span><span className="ek-text-muted">{gbp(a.day_rate_agreed)}/day</span></Row>
        ))}
      </Card>
    </div>
  );
}

/* ------------------------------- Intelligence --------------------------- */
function Intelligence({ onOpenAccount }) {
  const [nba, setNba] = useState(null);
  const [scores, setScores] = useState([]);
  const [heat, setHeat] = useState([]);
  useEffect(() => {
    api.get("/admin/crm/intelligence/next-best-actions", { params: { use_ai: true } }).then(({ data }) => setNba(data)).catch(() => setNba(false));
    api.get("/admin/crm/intelligence/scores").then(({ data }) => setScores(data.scores)).catch(() => {});
    api.get("/admin/crm/intelligence/heatmap").then(({ data }) => setHeat(data.heatmap)).catch(() => {});
  }, []);
  const actions = nba ? (nba.ai_suggestions && nba.ai_suggestions.length ? nba.ai_suggestions : nba.rule_based) : [];
  const acctName = {}; heat.forEach((h) => { acctName[h.id] = h.name; });
  const bandColor = (b) => b === "hot" ? "bg-emerald-100 text-emerald-700" : b === "warm" ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700";
  return (
    <div className="space-y-6" data-testid="crm-intelligence">
      <Card title="Next best actions" icon={Target} testid="card-nba"
        action={nba?.ai_used && <span className="text-[11px] text-[#7737F6] flex items-center gap-1"><Sparkles size={12} /> AI-assisted</span>}>
        {nba === null ? <Loading /> : (actions.length === 0 ? <Empty text="All clear — no actions flagged" /> : actions.map((x, i) => (
          <div key={i} className="flex items-start gap-3 py-2 text-sm border-b border-zinc-50 last:border-0" data-testid={`nba-item-${i}`}>
            <span className={`text-[10px] px-2 py-0.5 rounded-full h-fit ${x.priority === "high" ? "bg-red-100 text-red-700" : "ek-surface-2 ek-text-soft"}`}>{x.priority || "med"}</span>
            <div><div className="ek-text">{x.action}</div>{x.why && <div className="text-xs ek-text-muted">{x.why}</div>}</div>
          </div>
        )))}
      </Card>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card title="Top opportunities by score" icon={TrendingUp} testid="card-scores">
          {scores.slice(0, 8).map((s) => (
            <button key={s.id} onClick={() => onOpenAccount(s.account_id)} data-testid={`score-${s.id}`}
              className="w-full flex items-center justify-between py-2 text-sm border-b border-zinc-50 last:border-0 text-left hover:bg-zinc-50">
              <span className="truncate"><span className="text-[#7737F6]">{s.name}</span>{acctName[s.account_id] && <span className="ek-text-muted"> · {acctName[s.account_id]}</span>}</span>
              <span className="flex items-center gap-2 shrink-0"><span className="text-xs ek-text-muted">{gbp(s.est_value)}</span>
                <span className="font-semibold text-[#7737F6] w-8 text-right">{s.score}</span></span>
            </button>
          ))}
          {scores.length === 0 && <Empty text="No open opportunities" />}
        </Card>
        <Card title="Relationship heatmap" icon={Flame} testid="card-heatmap">
          {heat.slice(0, 10).map((h) => (
            <div key={h.id} className="flex items-center justify-between py-2 text-sm border-b border-zinc-50 last:border-0" data-testid={`heat-${h.id}`}>
              <button onClick={() => onOpenAccount(h.id)} className="text-[#7737F6] hover:underline truncate text-left">{h.name}</button>
              <span className="flex items-center gap-2 text-xs ek-text-muted">{h.contacts} contacts
                <span className={`px-2 py-0.5 rounded-full ${bandColor(h.band)}`}>{h.band}</span></span>
            </div>
          ))}
          {heat.length === 0 && <Empty text="No accounts" />}
        </Card>
      </div>
    </div>
  );
}

function AccountPlan({ account, canWrite }) {
  const [p, setP] = useState(null);
  const [edit, setEdit] = useState(false);
  const [f, setF] = useState({});
  useEffect(() => {
    api.get(`/admin/crm/intelligence/account-plan/${account.id}`).then(({ data }) => { setP(data); setF(data.exists === false ? {} : data); }).catch(() => setP(false));
  }, [account.id]);
  const save = async () => {
    try {
      const { data } = await api.put(`/admin/crm/intelligence/account-plan/${account.id}`, {
        strategy: f.strategy || "", business_issues: f.business_issues || "",
        transformation_priorities: f.transformation_priorities || "",
        target_opportunities: f.target_opportunities || "", competitors: f.competitors || "",
        next_actions: f.next_actions || "", annual_revenue_potential: Number(f.annual_revenue_potential) || 0,
      });
      setP(data); setF(data); setEdit(false); toast.success("Account plan saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const FIELDS = [["strategy", "Client strategy"], ["business_issues", "Known business issues"],
    ["transformation_priorities", "Transformation priorities"], ["target_opportunities", "Target opportunities"],
    ["competitors", "Competitor presence"], ["next_actions", "Next best actions"]];
  return (
    <Card title="Account plan" icon={Target} testid="card-account-plan"
      action={canWrite && <SmallBtn onClick={() => setEdit((v) => !v)} testid="crm-plan-edit">{edit ? "Close" : "Edit"}</SmallBtn>}>
      {p === null ? <Loading /> : edit ? (
        <div className="space-y-2" data-testid="crm-plan-form">
          {FIELDS.map(([k, label]) => (
            <Field key={k} label={label}><textarea data-testid={`crm-plan-${k}`} value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} className={inp} rows={2} /></Field>
          ))}
          <Field label="Annual revenue potential (£)"><input type="number" inputMode="decimal" data-testid="crm-plan-revenue" value={f.annual_revenue_potential || ""} onChange={(e) => setF({ ...f, annual_revenue_potential: e.target.value })} className={inp} /></Field>
          <button onClick={save} data-testid="crm-plan-save" className="px-4 py-2 text-sm bg-[#7737F6] text-white rounded-lg">Save plan</button>
        </div>
      ) : (p.exists === false ? <Empty text="No account plan yet" /> : (
        <div className="space-y-2 text-sm">
          {FIELDS.map(([k, label]) => p[k] ? <div key={k}><span className="text-xs ek-text-muted">{label}</span><div className="ek-text-soft">{p[k]}</div></div> : null)}
          {p.annual_revenue_potential ? <div><span className="text-xs ek-text-muted">Annual revenue potential</span><div className="ek-text-soft font-medium">{gbp(p.annual_revenue_potential)}</div></div> : null}
        </div>
      ))}
    </Card>
  );
}

/* ------------------------------- Integrations --------------------------- */
function Integrations({ canWrite }) {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState("");
  const load = useCallback(() => {
    api.get("/admin/crm/integrations/status").then(({ data }) => setS(data)).catch(() => setS(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const connect = async (provider) => {
    setBusy(provider);
    try {
      const { data } = await api.get(`/admin/crm/integrations/${provider}/connect`);
      window.location.href = data.authorize_url;
    } catch (e) {
      toast.error(e.response?.data?.detail === `${provider}_not_configured`
        ? "Not configured yet — add credentials to the server." : "Could not start connection");
      setBusy("");
    }
  };
  const disconnect = async (provider, label) => {
    setBusy(provider);
    try { await api.post(`/admin/crm/integrations/${provider}/disconnect`); toast.success(`${label} disconnected`); load(); }
    catch { toast.error("Failed"); } finally { setBusy(""); }
  };
  const syncPayments = async () => {
    setBusy("qb-sync");
    try { const { data } = await api.post("/admin/crm/integrations/quickbooks/sync-payments"); toast.success(`Checked ${data.checked} invoice(s) · ${data.paid} paid`); }
    catch (e) { toast.error(e.response?.data?.detail || "Sync failed"); } finally { setBusy(""); }
  };
  const syncM365 = async () => {
    setBusy("ms-sync");
    try { const { data } = await api.post("/admin/crm/integrations/m365/sync"); toast.success(`${data.emails_logged} emails · ${data.events_logged} meetings captured (${data.contact_matches} matches)`); }
    catch (e) { toast.error(e.response?.data?.detail || "Sync failed"); } finally { setBusy(""); }
  };

  if (s === null) return <Loading />;
  if (s === false) return <ErrorState />;
  const qb = s.quickbooks, ms = s.m365;

  return (
    <div className="space-y-6 max-w-3xl" data-testid="crm-integrations">
      {/* QuickBooks */}
      <div className="border ek-line rounded-xl ek-surface p-5" data-testid="integration-quickbooks">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center"><Receipt size={20} className="text-emerald-600" /></div>
            <div>
              <div className="font-semibold ek-text flex items-center gap-2">QuickBooks Online
                <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded ek-surface-2 ek-text-muted">{qb.environment}</span></div>
              <div className="text-xs ek-text-muted">Push milestone invoices and sync payment status.</div>
            </div>
          </div>
          <StatusPill connected={qb.connected} />
        </div>
        {qb.connected ? (
          <div className="mt-4 space-y-2">
            <div className="text-sm ek-text-soft">{qb.company_name ? <><b>{qb.company_name}</b> · </> : ""}Realm {qb.realm_id} · connected {(qb.connected_at || "").slice(0, 10)}</div>
            {canWrite && (
              <div className="flex gap-2 flex-wrap">
                <button onClick={syncPayments} disabled={busy === "qb-sync"} data-testid="qb-sync-payments"
                  className="flex items-center gap-1.5 text-xs font-medium text-white bg-[#7737F6] hover:bg-[#5D28C7] px-3 py-1.5 rounded-lg disabled:opacity-50">
                  <RefreshCw size={13} className={busy === "qb-sync" ? "animate-spin" : ""} /> Sync payments</button>
                <button onClick={() => disconnect("quickbooks", "QuickBooks")} data-testid="qb-disconnect"
                  className="flex items-center gap-1.5 text-xs font-medium text-red-600 border border-red-200 px-3 py-1.5 rounded-lg hover:bg-red-50">
                  <Unplug size={13} /> Disconnect</button>
              </div>
            )}
          </div>
        ) : (
          <div className="mt-4">
            {qb.configured
              ? (canWrite && <button onClick={() => connect("quickbooks")} disabled={busy === "quickbooks"} data-testid="qb-connect"
                  className="flex items-center gap-1.5 text-sm font-medium text-white bg-[#7737F6] hover:bg-[#5D28C7] px-4 py-2 rounded-lg disabled:opacity-50">
                  <Plug size={15} /> Connect QuickBooks</button>)
              : <NotConfigured provider="QBO_CLIENT_ID / QBO_CLIENT_SECRET" />}
          </div>
        )}
      </div>

      {/* Microsoft 365 */}
      <div className="border ek-line rounded-xl ek-surface p-5" data-testid="integration-m365">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-sky-50 flex items-center justify-center"><Mail size={20} className="text-sky-600" /></div>
            <div>
              <div className="font-semibold ek-text">Microsoft 365</div>
              <div className="text-xs ek-text-muted">Connect your Outlook to auto-capture emails & meetings with known contacts.</div>
            </div>
          </div>
          <StatusPill connected={ms.connected_self} />
        </div>
        {ms.connected_self ? (
          <div className="mt-4 space-y-2">
            <div className="text-sm ek-text-soft">Connected as <b>{ms.self_mailbox}</b></div>
            {canWrite && (
              <div className="flex gap-2 flex-wrap">
                <button onClick={syncM365} disabled={busy === "ms-sync"} data-testid="ms-sync"
                  className="flex items-center gap-1.5 text-xs font-medium text-white bg-[#7737F6] hover:bg-[#5D28C7] px-3 py-1.5 rounded-lg disabled:opacity-50">
                  <RefreshCw size={13} className={busy === "ms-sync" ? "animate-spin" : ""} /> Sync now</button>
                <button onClick={() => disconnect("m365", "Microsoft 365")} data-testid="ms-disconnect"
                  className="flex items-center gap-1.5 text-xs font-medium text-red-600 border border-red-200 px-3 py-1.5 rounded-lg hover:bg-red-50">
                  <Unplug size={13} /> Disconnect</button>
              </div>
            )}
          </div>
        ) : (
          <div className="mt-4">
            {ms.configured
              ? (canWrite && <button onClick={() => connect("m365")} disabled={busy === "m365"} data-testid="ms-connect"
                  className="flex items-center gap-1.5 text-sm font-medium text-white bg-[#7737F6] hover:bg-[#5D28C7] px-4 py-2 rounded-lg disabled:opacity-50">
                  <Plug size={15} /> Connect Outlook</button>)
              : <NotConfigured provider="MS_CLIENT_ID / MS_CLIENT_SECRET" />}
          </div>
        )}
        {ms.connected_mailboxes?.length > 0 && (
          <div className="mt-4 pt-3 border-t ek-line">
            <div className="text-xs ek-text-muted mb-1">Connected mailboxes on this tenant</div>
            {ms.connected_mailboxes.map((m) => (
              <div key={m.user_email} className="flex items-center gap-2 text-xs ek-text-soft py-0.5">
                <Check size={12} className="text-emerald-500" /> {m.mailbox || m.user_email}</div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const StatusPill = ({ connected }) => (
  <span className={`text-[11px] px-2.5 py-1 rounded-full font-medium ${connected ? "bg-emerald-100 text-emerald-700" : "ek-surface-2 ek-text-muted"}`}>
    {connected ? "Connected" : "Not connected"}</span>);
const NotConfigured = ({ provider }) => (
  <div className="text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2" data-testid="integration-not-configured">
    Not configured — add <code className="font-mono">{provider}</code> to the backend environment, then reload.</div>);

/* ------------------------------- Primitives ------------------------------ */
const inp = "w-full px-3 py-2 text-sm border ek-line rounded-lg focus:outline-none focus:border-[#7737F6]";
const Field = ({ label, children }) => (<label className="block"><span className="text-xs ek-text-muted mb-1 block">{label}</span>{children}</label>);
const Loading = () => <div className="py-12 text-center text-sm ek-text-muted" data-testid="crm-loading">Loading…</div>;
const ErrorState = () => <div className="py-12 text-center text-sm text-red-500">Could not load. Check your access.</div>;
const Empty = ({ text }) => <div className="py-6 text-center text-sm ek-text-muted">{text}</div>;
const Row = ({ children }) => <div className="flex items-center justify-between py-2 text-sm border-b border-zinc-50 last:border-0">{children}</div>;
const SmallBtn = ({ onClick, children, testid }) => (
  <button onClick={onClick} data-testid={testid} className="flex items-center gap-1 text-xs font-medium text-[#7737F6] hover:text-[#5D28C7]"><Plus size={13} />{children}</button>);
const Stat = ({ label, value, icon: Icon, testid }) => (
  <div className="p-4 border ek-line rounded-xl ek-surface" data-testid={testid}>
    <div className="flex items-center gap-2 ek-text-muted text-xs mb-2"><Icon size={14} /> {label}</div>
    <div className="text-2xl font-bold ek-text">{value}</div></div>);
const Card = ({ title, icon: Icon, children, action, testid }) => (
  <div className="border ek-line rounded-xl ek-surface p-4" data-testid={testid}>
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-2 text-sm font-semibold ek-text">{Icon && <Icon size={16} className="text-[#7737F6]" />}{title}</div>
      {action}</div>{children}</div>);
